// API Keys admin — service tokens for non-human callers (your SaaS apps,
// scheduled jobs, n8n, etc.).
//
// UX priorities:
//   - "Created!" reveal is unmistakably one-shot — banner + countdown of
//     copies-to-clipboard-attempts? No, simpler: lock the dialog open until
//     they confirm they've saved it, with a strong warning.
//   - Each row makes the scope visible at a glance: which projects, which
//     tiers, which tasks (if narrowed). No clicking through to learn what
//     a key can do.
//   - Revoked rows are visually de-emphasized but kept visible (audit value).
//   - Empty state explains what these are and walks the user to creation.
import { useEffect, useMemo, useState } from "react";
import {
  KeyRound,
  Plus,
  Copy,
  Check,
  AlertTriangle,
  Loader2,
  Trash2,
  ShieldCheck,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  createServiceToken,
  listProjects,
  listServiceTokens,
  listTasks,
  revokeServiceToken,
  type CreatedServiceToken,
  type ProjectSummary,
  type ServiceTokenSummary,
  type TaskSummary,
} from "@/lib/api";

type Tier = "safe" | "side-effect" | "destructive";
const TIER_OPTIONS: Tier[] = ["safe", "side-effect", "destructive"];

export function AdminApiKeysPage() {
  const [tokens, setTokens] = useState<ServiceTokenSummary[] | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedServiceToken | null>(null);

  async function refresh() {
    try {
      const [t, p, ts] = await Promise.all([
        listServiceTokens(),
        listProjects(),
        listTasks(),
      ]);
      setTokens(t);
      setProjects(p);
      setTasks(ts);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleRevoke(token: ServiceTokenSummary) {
    if (
      !confirm(
        `Revoke "${token.name}"?\n\nAny service using this key will start receiving 401s on its next request. This cannot be undone.`
      )
    ) {
      return;
    }
    await revokeServiceToken(token.id);
    await refresh();
  }

  return (
    <div className="container py-8">
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">API Keys</h1>
          </div>
          <p className="max-w-xl text-sm text-muted-foreground">
            Long-lived credentials for non-human callers — your SaaS chatbot,
            scheduled jobs, anything that hits{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              /api/v1/trigger
            </code>{" "}
            with{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              Authorization: Bearer …
            </code>
            .
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" /> New API key
            </Button>
          </DialogTrigger>
          <CreateDialog
            projects={projects}
            tasks={tasks}
            onCreated={async (result) => {
              setCreateOpen(false);
              setCreated(result);
              await refresh();
            }}
          />
        </Dialog>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {!tokens && (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {tokens && tokens.length === 0 && (
        <EmptyState onCreate={() => setCreateOpen(true)} />
      )}

      {tokens && tokens.length > 0 && (
        <div className="space-y-3">
          {tokens.map((t) => (
            <TokenCard
              key={t.id}
              token={t}
              projects={projects}
              onRevoke={handleRevoke}
            />
          ))}
        </div>
      )}

      <ResultDialog
        token={created}
        onClose={() => setCreated(null)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-lg border border-dashed bg-card p-12 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        <KeyRound className="h-6 w-6" />
      </div>
      <h2 className="mt-4 text-lg font-semibold">No API keys yet</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Create a key to let your other apps trigger tasks here. Each key is
        scoped to specific projects, tiers, and (optionally) individual tasks
        — there's no master key.
      </p>
      <Button className="mt-6" onClick={onCreate}>
        <Plus className="mr-2 h-4 w-4" /> Create your first key
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Token row
// ─────────────────────────────────────────────────────────────────────────

function TokenCard({
  token,
  projects,
  onRevoke,
}: {
  token: ServiceTokenSummary;
  projects: ProjectSummary[];
  onRevoke: (t: ServiceTokenSummary) => void;
}) {
  const isRevoked = Boolean(token.revokedAt);
  const projectsById = useMemo(
    () => new Map(projects.map((p) => [p.id, p])),
    [projects]
  );

  return (
    <div
      className={
        "rounded-lg border bg-card p-4 transition-opacity " +
        (isRevoked ? "opacity-60" : "")
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <span className="font-medium">{token.name}</span>
            {isRevoked ? (
              <Badge variant="destructive">revoked</Badge>
            ) : (
              <Badge variant="success">active</Badge>
            )}
          </div>
          <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
            {token.display}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 text-xs">
            <ScopeGroup label="Projects">
              {token.projectIds.map((id) => {
                const p = projectsById.get(id);
                return (
                  <Badge key={id} variant="outline">
                    {p?.icon} {p?.name ?? id}
                  </Badge>
                );
              })}
            </ScopeGroup>
            <ScopeGroup label="Tiers">
              {token.allowedTiers.map((tier) => (
                <Badge
                  key={tier}
                  variant={
                    tier === "safe"
                      ? "success"
                      : tier === "side-effect"
                        ? "info"
                        : "warning"
                  }
                >
                  {tier}
                </Badge>
              ))}
            </ScopeGroup>
            {token.allowedTaskIds && (
              <ScopeGroup label="Tasks">
                {token.allowedTaskIds.length === 0 ? (
                  <span className="text-muted-foreground">none</span>
                ) : (
                  token.allowedTaskIds.map((id) => (
                    <Badge key={id} variant="outline" className="font-mono">
                      {id}
                    </Badge>
                  ))
                )}
              </ScopeGroup>
            )}
            {!token.allowedTaskIds && (
              <ScopeGroup label="Tasks">
                <span className="text-muted-foreground">all in projects</span>
              </ScopeGroup>
            )}
          </div>
          <div className="flex items-center gap-4 pt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" />
              {token.rateLimitPerMin}/min
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              created {new Date(token.createdAt).toLocaleDateString()}
            </span>
            <span>
              last used:{" "}
              {token.lastUsedAt
                ? new Date(token.lastUsedAt).toLocaleString()
                : "never"}
            </span>
            {token.ownerEmail && <span>· owner: {token.ownerEmail}</span>}
          </div>
        </div>
        {!isRevoked && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onRevoke(token)}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" /> Revoke
          </Button>
        )}
      </div>
    </div>
  );
}

function ScopeGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">{label}:</span>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Create dialog
// ─────────────────────────────────────────────────────────────────────────

function CreateDialog({
  projects,
  tasks,
  onCreated,
}: {
  projects: ProjectSummary[];
  tasks: TaskSummary[];
  onCreated: (result: CreatedServiceToken) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [tiers, setTiers] = useState<Tier[]>(["safe"]);
  const [taskMode, setTaskMode] = useState<"all" | "specific">("all");
  const [allowedTaskIds, setAllowedTaskIds] = useState<string[]>([]);
  const [rateLimit, setRateLimit] = useState(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eligibleTasks = useMemo(
    () =>
      tasks.filter(
        (t) => projectIds.includes(t.project) && t.tier !== "god"
      ),
    [tasks, projectIds]
  );

  function toggle<T>(value: T, list: T[], set: (l: T[]) => void) {
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (projectIds.length === 0) {
      setError("Pick at least one project.");
      return;
    }
    if (tiers.length === 0) {
      setError("Pick at least one tier.");
      return;
    }
    setBusy(true);
    try {
      const result = await createServiceToken({
        name: name.trim(),
        projectIds,
        allowedTiers: tiers,
        allowedTaskIds: taskMode === "specific" ? allowedTaskIds : undefined,
        rateLimitPerMin: rateLimit,
      });
      await onCreated(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle>New API key</DialogTitle>
        <DialogDescription>
          Configure the scope. The token itself will be shown once after
          you submit.
        </DialogDescription>
      </DialogHeader>

      <form className="space-y-5" onSubmit={submit}>
        <div className="space-y-1.5">
          <Label htmlFor="kname">Name</Label>
          <Input
            id="kname"
            placeholder="acme-chatbot-prod"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
          />
          <p className="text-xs text-muted-foreground">
            For your own bookkeeping. Shows up in the audit log on every
            call this token makes.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Projects</Label>
          <div className="flex flex-wrap gap-1.5">
            {projects.length === 0 && (
              <span className="text-xs text-muted-foreground">
                No projects available.
              </span>
            )}
            {projects.map((p) => {
              const on = projectIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggle(p.id, projectIds, setProjectIds)}
                  className={
                    "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm transition-colors " +
                    (on
                      ? "border-primary bg-primary text-primary-foreground"
                      : "hover:bg-muted")
                  }
                >
                  <span aria-hidden>{p.icon}</span>
                  {p.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Allowed tiers</Label>
          <div className="flex flex-wrap gap-1.5">
            {TIER_OPTIONS.map((tier) => {
              const on = tiers.includes(tier);
              return (
                <button
                  key={tier}
                  type="button"
                  onClick={() => toggle(tier, tiers, setTiers)}
                  className={
                    "rounded-md border px-2.5 py-1 text-sm transition-colors " +
                    (on
                      ? "border-primary bg-primary text-primary-foreground"
                      : "hover:bg-muted")
                  }
                >
                  {tier}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Service tokens can never trigger god-tier tasks regardless of
            this setting.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Task scope</Label>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setTaskMode("all")}
              className={
                "flex-1 rounded-md border px-3 py-2 text-left text-sm transition-colors " +
                (taskMode === "all"
                  ? "border-primary bg-primary/5"
                  : "hover:bg-muted")
              }
            >
              <div className="font-medium">Any task in projects</div>
              <div className="text-xs text-muted-foreground">
                Permissive — limited only by tier.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setTaskMode("specific")}
              className={
                "flex-1 rounded-md border px-3 py-2 text-left text-sm transition-colors " +
                (taskMode === "specific"
                  ? "border-primary bg-primary/5"
                  : "hover:bg-muted")
              }
            >
              <div className="font-medium">Specific tasks only</div>
              <div className="text-xs text-muted-foreground">
                Stricter — pick exactly which task IDs to allow.
              </div>
            </button>
          </div>
          {taskMode === "specific" && (
            <div className="rounded-md border p-2">
              {eligibleTasks.length === 0 ? (
                <p className="px-1 text-xs text-muted-foreground">
                  No matching tasks. Pick a project above first.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {eligibleTasks.map((t) => {
                    const on = allowedTaskIds.includes(t.taskId);
                    return (
                      <button
                        key={t.taskId}
                        type="button"
                        onClick={() =>
                          toggle(t.taskId, allowedTaskIds, setAllowedTaskIds)
                        }
                        className={
                          "rounded-md border px-2 py-1 font-mono text-xs transition-colors " +
                          (on
                            ? "border-primary bg-primary text-primary-foreground"
                            : "hover:bg-muted")
                        }
                      >
                        {t.taskId}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="rate">Rate limit (per minute)</Label>
          <Input
            id="rate"
            type="number"
            min={1}
            max={10000}
            value={rateLimit}
            onChange={(e) => setRateLimit(Number(e.target.value))}
          />
          <p className="text-xs text-muted-foreground">
            Per-token cap. Sane default: 60 — budget for one trigger per
            second on average.
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? "Creating…" : "Create key"}
        </Button>
      </form>
    </DialogContent>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Result modal — shown ONCE after creation
// ─────────────────────────────────────────────────────────────────────────

function ResultDialog({
  token,
  onClose,
}: {
  token: CreatedServiceToken | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    setCopied(false);
    setAcknowledged(false);
  }, [token]);

  if (!token) return null;

  async function copy() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API unavailable; fall back to selecting text
    }
  }

  return (
    <Dialog open={Boolean(token)} onOpenChange={(open) => !open && acknowledged && onClose()}>
      <DialogContent
        className="max-w-xl"
        onInteractOutside={(e) => {
          if (!acknowledged) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (!acknowledged) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Check className="h-5 w-5 text-success" />
            Key created — copy it now
          </DialogTitle>
          <DialogDescription>
            <strong className="text-foreground">{token.name}</strong> is
            scoped to {token.projectIds.length} project
            {token.projectIds.length === 1 ? "" : "s"} and{" "}
            {token.allowedTiers.length} tier
            {token.allowedTiers.length === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div>
              <div className="font-medium">
                This is the only time the full token will be shown.
              </div>
              <p className="mt-1 text-muted-foreground">
                Copy it into your service's secret store now. We don't keep
                a copy — only a hash. If you lose it, you'll need to revoke
                this key and create a new one.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-md border bg-muted/30">
          <div className="flex items-center gap-2 border-b px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Token
            <button
              onClick={copy}
              className="ml-auto inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs hover:bg-muted"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-success" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Copy
                </>
              )}
            </button>
          </div>
          <div className="overflow-x-auto p-3">
            <code className="font-mono text-sm break-all">{token.token}</code>
          </div>
        </div>

        <div className="rounded-md bg-muted/30 p-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            How to use it
          </div>
          <pre className="overflow-x-auto rounded bg-background p-2 font-mono text-xs">{`curl -X POST https://jobsgoblin.com/api/v1/trigger \\
  -H "Authorization: Bearer ${token.token.slice(0, 22)}…" \\
  -H "Content-Type: application/json" \\
  -d '{"taskId":"local-chat","payload":{"message":"hi"}}'`}</pre>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span>
            I've copied the token to my secret store. I understand that
            closing this dialog discards it forever.
          </span>
        </label>

        <Button onClick={onClose} disabled={!acknowledged} className="w-full">
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
}
